import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { stat } from 'fs';

@Component({
  selector: 'app-status-badge',
  templateUrl: './status-badge.component.html',
  styleUrls: ['./status-badge.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class StatusBadgeComponent {
  @Input() text: string = '';
  @Input() color: string = '#000000';
  @Input() isPayment: boolean = false;
  @Input() isBooking: boolean = false;
  @Input() isLprLogDirection: boolean = false;

  ngOnChanges(): void {
    if (this.isPayment || this.isLprLogDirection) {
      this.color = this.getStatusColor(this.text);
    }
    else if (this.isBooking) {
      this.color = this.getBookingStatusColor(this.text);
    }
  }

  getStatusColor(status: string): string {
    switch (status) {
      // New/Initial states - Blue
      case 'New':
        return '#2196F3'; // Light blue
      case 'Reserved':
        return '#03A9F4'; // Slightly darker blue

      // In-progress states - Yellow/Orange
      case 'Checkin':
        return '#FFC107'; // Amber
      case 'Checkout':
        return '#FF9800'; // Orange

      // Positive/Completed states - Green
      case 'Paid':
        return '#4CAF50'; // Green
      case 'Delivered':
        return '#388E3C'; // Darker green
      case 'Done':
        return '#2E7D32'; // Even darker green

      // Warning/Additional action needed - Yellow
      case 'CheckoutAdditional':
        return '#FFC107'; // Amber

      // Cancelled/Error states - Red
      case 'Cancelled':
        return '#F44336'; // Red
      case 'NoShow':
        return '#E91E63'; // Pinkish red
      case 'Return':
        return '#D32F2F'; // Dark red

      //Additional Services
      case "Planned":
        return '#3f51b5';
      case "Underway":
        return '#ffc107';
      case "Completed":
        return '#28a745';
      case 'Removed':
        return '#F44336';

      //LPR Log directions
      case 'In':
        return '#4CAF50'; // Green
      case 'Out':
        return '#F44336'; // Red


      // Default fallback
      default:
        return '#9E9E9E'; // Grey
    }
  }

  getBookingStatusColor(status: string): string {
    switch (status) {
      case 'Active':
        return '#28a745';
      case 'Planned':
        return '#ffc107';
      default:
        return 'black';
    }
  }

  private colorToRgb(color: string): { r: number, g: number, b: number } {
    const temp = document.createElement('div');
    temp.style.color = color;
    document.body.appendChild(temp);

    const computedColor = getComputedStyle(temp).color;
    document.body.removeChild(temp);

    const rgbMatch = computedColor.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);

    if (rgbMatch) {
      return {
        r: parseInt(rgbMatch[1], 10),
        g: parseInt(rgbMatch[2], 10),
        b: parseInt(rgbMatch[3], 10)
      };
    }

    return { r: 0, g: 0, b: 0 };
  }

  isLightColor(color: string): boolean {
    try {
      const rgb = this.colorToRgb(color);
      const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;

      return luminance > 0.5;
    } catch (e) {
      return false;
    }
  }

  isVeryLight(color: string): boolean {
    try {
      const rgb = this.colorToRgb(color);
      const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
      return luminance > 0.8;
    } catch (e) {
      return false;
    }
  }

  getLighterShade(color: string, opacity: number): string {
    try {
      const rgb = this.colorToRgb(color);
      // const isLight = this.isLightColor(color);
      // const isVeryLight = this.isVeryLight(color);

      // if (isVeryLight) {
      //   const darkenFactor = 0.3;
      //   const r = Math.round(rgb.r * darkenFactor);
      //   const g = Math.round(rgb.g * darkenFactor);
      //   const b = Math.round(rgb.b * darkenFactor);
      //   return `rgba(${r}, ${g}, ${b}, ${opacity + 0.1})`;
      // } else if (isLight) {
      //   const darkenFactor = 0.6;
      //   const r = Math.round(rgb.r * darkenFactor);
      //   const g = Math.round(rgb.g * darkenFactor);
      //   const b = Math.round(rgb.b * darkenFactor);
      //   return `rgba(${r}, ${g}, ${b}, ${opacity})`;
      // } else {
      const r = Math.round(rgb.r + (255 - rgb.r) * (1 - opacity));
      const g = Math.round(rgb.g + (255 - rgb.g) * (1 - opacity));
      const b = Math.round(rgb.b + (255 - rgb.b) * (1 - opacity));
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
      // }
    } catch (e) {
      return `rgba(0, 0, 0, ${opacity})`;
    }
  }


  getTextColor(): string {
    if (this.isVeryLight(this.color)) {
      const rgb = this.colorToRgb(this.color);
      const darkenFactor = 0.2;
      return `rgb(${Math.round(rgb.r * darkenFactor)}, ${Math.round(rgb.g * darkenFactor)}, ${Math.round(rgb.b * darkenFactor)})`;
    }

    return this.color;
  }
}
